import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Modal from "react-modal";
import Select from "react-select";
import { useData } from '../context/DataContext';
import styles from './LlmConfigModal.module.css'; // Ensure this CSS file exists and is styled
import Spinner from './Spinner'; // Assuming you have a Spinner component

/**
 * LLMConfigModal Component
 *
 * A modal dialog displayed after the LLM configuration button on the Sidebar is clicked, allowing the user
 * to customize the standard LLM configurations to their preferences.Uses react-modal for accessibility.
 *
 * @param {object} props - Component props
 * @param {boolean} props.isOpen - Controls whether the modal is visible.
 * @param {Function} props.onRequestClose - Function called when the modal requests to be closed (e.g., Esc key, overlay click, Cancel button).
 * @param {string} props.prompt - The suggested text for the follow-up task.
 * @param {Function} props.onPromptChange - Handler for changes to the task text textarea.
 * @param {string|number} props.days - The number of days for the follow-up delay (input value, can be string).
 * @param {Function} props.onDaysChange - Handler for changes to the days input field.
 * @param {Function} props.onConfirm - Function called when the user confirms the action (creates task if days > 0).
 * @param {boolean} [props.isSubmitting=false] - Optional flag to disable inputs/buttons during submission.
 */
// --- react-select Styles ---
// (Ensure these styles match your dark theme and are comprehensive)
const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        backgroundColor: '#3a3a3a',
        borderColor: state.isFocused ? '#6f9aee' : '#555',
        boxShadow: state.isFocused ? '0 0 0 1px #6f9aee' : 'none',
        '&:hover': { borderColor: state.isFocused ? '#6f9aee' : '#777' },
        minHeight: '30px', height: '30px', cursor: 'pointer',
    }),
    valueContainer: (provided) => ({ ...provided, height: '30px', padding: '0 6px' }),
    input: (provided) => ({ ...provided, margin: '0px', color: '#e0e0e0' }),
    indicatorSeparator: () => ({ display: 'none' }),
    indicatorsContainer: (provided) => ({ ...provided, height: '30px' }),
    menu: (provided) => ({ ...provided, backgroundColor: '#2d2d2d', zIndex: 10, maxHeight: '160px', overflowY: 'auto'}), 
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? '#4a4a4a' : state.isFocused ? '#3a3a3a' : '#2d2d2d',
        color: '#e0e0e0', cursor: 'pointer',  height: '30px',            /* NEW: Fix option height */
        display: 'flex',           /* NEW: Use flex for alignment */
        alignItems: 'center',      /* NEW: Center content vertically */
        '&:active': { backgroundColor: '#4a4a4a' },
    }),
    singleValue: (provided) => ({ ...provided, color: '#e0e0e0' }),
    placeholder: (provided) => ({ ...provided, color: '#aaa' }),
    dropdownIndicator: (provided) => ({ ...provided, color: '#aaa', '&:hover': { color: '#ccc' } }),
    // Add styles for multi-select if used elsewhere, not needed here
};

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

const LlmConfigModal = () => {
  const {
    // Modal State & Control
    isLlmConfigModalOpen,
    setIsLlmConfigModalOpen,
    llmConfigModalLoading,
    llmConfigModalError,
    setLlmConfigModalError,

    // Data from Context
    taskTemplates,
    llmConfigs,
    userSettings,

    // Context Functions
    updateTaskTemplate,
    updateUserSettings,
    fetchUserSettings,
    fetchUserApiKeyStatus,
    upsertUserApiKey,
    deleteUserApiKey,
  } = useData();

  // --- Local State ---
  const [selectedTaskType, setSelectedTaskType] = useState("");
  const [currentProvider, setCurrentProvider] = useState(null);
  const [currentIdiom, setCurrentIdiom] = useState(null);
  const [currentModel, setCurrentModel] = useState(null);
  


  // --- NEW API Key State ---
  const [currentApiKey, setCurrentApiKey] = useState(""); // Stores the entered key
  const [apiKeyExists, setApiKeyExists] = useState(false); // Does a key exist in DB for current provider?
  const [isApiKeyLoading, setIsApiKeyLoading] = useState(false); // Loading for API key fetch/save
  const [apiKeyError, setApiKeyError] = useState(null); // Error specific to API key operations
  const [isApiKeyInputVisible, setIsApiKeyInputVisible] = useState(false); // Control input visibility
  const [isDeletingApiKey, setIsDeletingApiKey] = useState(false);

  // Prompt overrides
  const [currentPrompt, setCurrentPrompt] = useState("");
  const [currentFollowUpText, setCurrentFollowUpText] = useState("");
  const [currentFollowUpPrompt, setCurrentFollowUpPrompt] = useState("");
  const [currentFollowUpDaysOffset, setCurrentFollowUpDaysOffset] = useState(0);

  // UI feedback
  const [successMessage, setSuccessMessage] = useState("");
  const [isSettingDefault, setIsSettingDefault] = useState(false);

   // --- Track Previous State ---
  const prevProvider = usePrevious(currentProvider);
  const prevIdiom = usePrevious(currentIdiom);

  // --- Helper: Fetch API Key Status ---
  const checkApiKeyStatus = useCallback(async (provider) => {
    if (!provider?.value) {
        setApiKeyExists(false);
        setIsApiKeyInputVisible(false); // Hide if no provider selected
        setCurrentApiKey(""); // Clear any entered key
        setApiKeyError(null);
        return;
    }
    setIsApiKeyLoading(true);
    setApiKeyError(null);
    setCurrentApiKey(""); // Clear previously entered key on provider change

    console.log(`[checkApiKeyStatus] Checking for provider: ${provider.value}`);
    const result = await fetchUserApiKeyStatus(provider.value);
    setIsApiKeyLoading(false);

    if (result?.status && result.data) {
        console.log(`[checkApiKeyStatus] Result for ${provider.value}:`, result.data);
        setApiKeyExists(result.data.apiKeyExists);
        setIsApiKeyInputVisible(!result.data.apiKeyExists); // Show input ONLY if key does NOT exist
    } else {
        console.error("[checkApiKeyStatus] Failed to fetch API key status:", result?.message);
        setApiKeyError(result?.message || "Failed to check API key status.");
        setApiKeyExists(false); // Assume doesn't exist on error
        setIsApiKeyInputVisible(true); // Show input on error to allow entry
    }
  }, [fetchUserApiKeyStatus]); // Add dependency

  // --- Memos ---
  const templateOptions = useMemo(() => {
    return taskTemplates.map((template) => ({
      value: template.taskType,
      label: template.nameMenu,
    }));
  }, [taskTemplates]);

  // IMPORTANT: Extra logging here to see if we’re actually getting valid providers
  const providerOptions = useMemo(() => {
    const providers = Object.keys(llmConfigs || {}).sort();
    console.log("[providerOptions memo] llmConfigs keys:", providers);
    const mappedProviders = providers.map((p) => ({ value: p, label: p }));
    console.log("[providerOptions memo] mappedProviders:", mappedProviders);
    return mappedProviders;
  }, [llmConfigs]);

  const availableIdiomsForProvider = useMemo(() => {
    if (!currentProvider?.value || !llmConfigs?.[currentProvider.value]) {
      return [];
    }
    const idioms = Object.keys(llmConfigs[currentProvider.value]).sort();
    return idioms.map((i) => ({ value: i, label: i }));
  }, [currentProvider, llmConfigs]);

  const modelOptions = useMemo(() => {
    const providerVal = currentProvider?.value;
    const idiomVal = currentIdiom?.value;
  
    // If any required data is missing, return an empty array.
    if (!providerVal || !idiomVal || !llmConfigs?.[providerVal]?.[idiomVal]) {
      return [];
    }
  
    // Example shape: [{ llm_config_id: 101, model_name: 'gemini-2.0-flash-exp' }, ...]
    const modelArray = llmConfigs[providerVal][idiomVal];
  
    // Sort by model_name (alphabetically)
    const sorted = [...modelArray].sort((a, b) => 
      (a.model_name || '').localeCompare(b.model_name || '')
    );
  
    // Map each object to the shape your front-end expects
    return sorted.map((modelObj) => ({
      value: modelObj.model_name,
      label: modelObj.model_name,
      llmConfigId: modelObj.llm_config_id,
    }));
  }, [currentProvider, currentIdiom, llmConfigs]);
  
  

  const selectedTemplate = useMemo(() => {
    return taskTemplates?.find((t) => t.taskType === selectedTaskType);
  }, [taskTemplates, selectedTaskType]);

  const nextTaskPrompt = useMemo(() => {
    if (!selectedTemplate?.followUpTaskType)
      return "(No configured follow-up task)";
    const nextTask = taskTemplates?.find(
      (t) => t.taskType === selectedTemplate.followUpTaskType
    );
    return nextTask?.prompt || "(Follow-up task prompt not found)";
  }, [taskTemplates, selectedTemplate]);

  // --- Effects ---

// Effect 1: Handles the initial setup when the modal opens.
  // Effect 1: Handles the initial setup when the modal opens.
  useEffect(() => {
    if (isLlmConfigModalOpen) {
      console.log(`%c[Effect 1 - Init] Fired. Modal is opening.`, 'color: #7f00ff; font-weight: bold;');
      
      // Reset states on open
      setLlmConfigModalError(null);
      setSuccessMessage("");
      setSelectedTaskType("");
      setCurrentApiKey("");
      setApiKeyExists(false);
      setIsApiKeyInputVisible(false);
      setApiKeyError(null);

      console.log("[Effect 1 - Init] Determining initial state from userSettings:", userSettings);

      let initialProvider = null;
      let initialIdiom = null;
      let initialModel = null;

      // Primary Path: Attempt to find the user's saved default.
      if (userSettings?.defaultLlmConfigId && llmConfigs && Object.keys(llmConfigs).length > 0) {
        let found = false;
        const userDefaultId = Number(userSettings.defaultLlmConfigId);
        for (const provider in llmConfigs) {
          for (const idiom in llmConfigs[provider]) {
            const modelData = llmConfigs[provider][idiom].find(
              (m) => Number(m.llm_config_id) === userDefaultId
            );
            if (modelData) {
              initialProvider = { value: provider, label: provider };
              initialIdiom = { value: idiom, label: idiom };
              initialModel = { value: modelData.model_name, label: modelData.model_name, llmConfigId: modelData.llm_config_id };
              found = true; break;
            }
          }
          if (found) break;
        }
      }

      // *** THIS IS THE CRITICAL MISSING LOGIC ***
      // Fallback Path: If NO default was found, set a sensible starting point.
      if (!initialProvider && providerOptions.length > 0) {
        console.log("[Effect 1 - Init] No user default found. Applying fallback logic.");
        initialProvider = providerOptions[0];
        const firstIdiomOptions = Object.keys(llmConfigs?.[initialProvider.value] || {}).map((i) => ({ value: i, label: i }));
        if (firstIdiomOptions.length > 0) {
          initialIdiom = firstIdiomOptions[0];
          // When falling back, the model must be null. Let Effect 3 handle picking the first model.
          initialModel = null;
        }
      }
      // *** END OF MISSING LOGIC ***

      console.log("[Effect 1 - Init] Determined final initial values:", { provider: initialProvider?.value, idiom: initialIdiom?.value, model: initialModel?.value });
      
      setCurrentProvider(initialProvider);
      setCurrentIdiom(initialIdiom);
      setCurrentModel(initialModel);

      if (initialProvider) {
          checkApiKeyStatus(initialProvider);
      }
    }
  }, [isLlmConfigModalOpen, userSettings, llmConfigs, providerOptions]); // Added providerOptions dependency


  // Effect 2: Reacts ONLY to user-driven changes of the Provider.
  useEffect(() => {
    console.log(`%c[Effect 2 - Provider] Fired. Prev: ${prevProvider?.value}, Curr: ${currentProvider?.value}.`, 'color: #007bff;');

    // GUARD: Only run if the provider has meaningfully changed from one value to another.
    if (!prevProvider || prevProvider.value === currentProvider?.value) {
      console.log("[Effect 2 - Provider] GUARDED: No meaningful change detected.");
      return;
    }

    console.log("[Effect 2 - Provider] UNGUARDED: User changed provider. Resetting Idiom/Model.");
    const firstIdiom = availableIdiomsForProvider.length > 0 ? availableIdiomsForProvider[0] : null;
    setCurrentIdiom(firstIdiom);
    setCurrentModel(null);
    checkApiKeyStatus(currentProvider);

  }, [currentProvider, availableIdiomsForProvider]); // Added availableIdiomsForProvider


  // Effect 3: Reacts to changes in the Idiom.
  useEffect(() => {
    console.log(`%c[Effect 3 - Idiom] Fired. Prev: ${prevIdiom?.value}, Curr: ${currentIdiom?.value}.`, 'color: #28a745;');
    
    // GUARD: Do nothing if the idiom hasn't meaningfully changed.
    if (prevIdiom?.value === currentIdiom?.value) {
      console.log("[Effect 3 - Idiom] GUARDED: No meaningful change detected.");
      return;
    }
    
    console.log("[Effect 3 - Idiom] UNGUARDED: Idiom changed. Selecting new model.");

    // If we've just fallen back in Effect 1, initialModel will be null. This block will run.
    // If a user changes the idiom, this block will run.
    if (currentModel === null && modelOptions.length > 0) {
        console.log("[Effect 3 - Idiom] ACTION: Model is null, setting to first available option.");
        setCurrentModel(modelOptions[0]);
        return;
    }

    // If the currently selected model is NOT in the new list of options, reset it.
    const isCurrentModelValid = modelOptions.some(opt => opt.value === currentModel?.value);
    if (!isCurrentModelValid && modelOptions.length > 0) {
        console.log("[Effect 3 - Idiom] ACTION: Current model is invalid for this idiom, resetting to first available.");
        setCurrentModel(modelOptions[0]);
    }

  }, [currentIdiom, modelOptions, currentModel]); // Added currentModel

  // Effect 4: Populate task text fields
  useEffect(() => {
    if (!isLlmConfigModalOpen) return;
    setLlmConfigModalError(null);
    // setSuccessMessage("");
    if (selectedTemplate) {
      setCurrentPrompt(selectedTemplate.prompt || "");
      setCurrentFollowUpText(selectedTemplate.followUpText || "");
      setCurrentFollowUpPrompt(selectedTemplate.followUpPrompt || "");
      setCurrentFollowUpDaysOffset(selectedTemplate.followUpDaysOffset || 0);
                                                
    } else {
      setCurrentPrompt("");
      setCurrentFollowUpText("");
      setCurrentFollowUpPrompt("");
      setCurrentFollowUpDaysOffset(0);
    }
  }, [selectedTemplate, nextTaskPrompt, setLlmConfigModalError, isLlmConfigModalOpen]);

  // Handlers
  const handleCloseRequest = useCallback(() => {
    if (llmConfigModalLoading || isSettingDefault || 
        isApiKeyLoading || isDeletingApiKey) return;
    setIsLlmConfigModalOpen(false);
  }, [llmConfigModalLoading, isSettingDefault, setIsLlmConfigModalOpen, isApiKeyLoading,
      isDeletingApiKey ]);

  const handleTemplateChange = (selectedOption) => {
    setSelectedTaskType(selectedOption ? selectedOption.value : "");
  };
  const handleProviderChange = (selectedOption) => {
    console.log("[UI Action] Provider changed to:", selectedOption);
    setCurrentProvider(selectedOption);
  };
  const handleIdiomChange = (selectedOption) => {
    console.log("[UI Action] Idiom changed to:", selectedOption);
    setCurrentIdiom(selectedOption);
  };
  const handleModelChange = (selectedOption) => {
    console.log("[UI Action] Model changed to:", selectedOption);
    setCurrentModel(selectedOption);
  };
  
  const handleApiKeyChange = (event) => {
    setCurrentApiKey(event.target.value);
    // Clear API key error when user starts typing
    if (apiKeyError) {
        setApiKeyError(null);
    }
  };

  // --- NEW Handler for Deleting API Key ---
  const handleDeleteApiKey = async () => {
      if (!currentProvider?.value || !apiKeyExists || isOverallLoading) {
          return; // Don't delete if no provider, key doesn't exist, or loading
      }

      setApiKeyError(null); // Clear previous errors
      setSuccessMessage("");
      setIsDeletingApiKey(true);

      console.log(`[handleDeleteApiKey] Deleting API Key for provider: ${currentProvider.value}`);

      try {
          const result = await deleteUserApiKey(currentProvider.value);

          if (result?.status) {
               console.log("[handleDeleteApiKey] API Key deleted successfully.");
               setApiKeyExists(false); // Update state: key no longer exists
               setIsApiKeyInputVisible(true); // Show input to allow entering a new key
               setCurrentApiKey(""); // Clear any potentially stale key in state
               setSuccessMessage("API key deleted. You can now enter a new one.");
               setTimeout(() => setSuccessMessage(""), 3000);
          } else {
               console.error("[handleDeleteApiKey] Failed to delete API key:", result?.message);
               // Handle 404 specifically maybe? Although button should be hidden if !apiKeyExists
               if (result?.error === 'NOT_FOUND') {
                   setApiKeyError("Key already deleted or not found.");
                   setApiKeyExists(false); // Correct state if out of sync
                   setIsApiKeyInputVisible(true);
               } else {
                   setApiKeyError(result?.message || "Failed to delete API key.");
               }
          }
      } catch (error) {
          console.error("[handleDeleteApiKey] Unexpected error:", error);
          setApiKeyError("An unexpected error occurred while deleting the key.");
      } finally {
          setIsDeletingApiKey(false); // Turn off delete loading state
      }
  };


  const handleSaveChanges = async () => {
    if (!selectedTaskType || llmConfigModalLoading || isSettingDefault) return;
    setLlmConfigModalError(null);
    setSuccessMessage("");
    const overrideData = {
      prompt: currentPrompt,
      followUpText: currentFollowUpText,
      followUpPrompt: currentFollowUpPrompt,
      followUpDaysOffset: currentFollowUpDaysOffset,
    };

    console.log(
      `[handleSaveChanges] Saving user override for ${selectedTaskType}:`,
      overrideData
    );
    const result = await updateTaskTemplate(selectedTaskType, overrideData);

    if (result?.status) {
      setSuccessMessage("Your prompt changes saved successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    }
    // If error, context sets llmConfigModalError
  };

   // --- UPDATED: Handler for Set Default LLM ---
   const handleSetDefaultLlm = async () => {
    setApiKeyError(null); // Clear previous API key errors
    setLlmConfigModalError(null); // Clear general errors
    setSuccessMessage("");

    // 1. Validate selection
    if (!currentProvider || !currentIdiom || !currentModel?.llmConfigId) {
      setLlmConfigModalError("Please select a valid Provider, Idiom, and Model.");
      return;
    }

    // --- FIX: Look up the correct llmConfigId based on current selection ---
    let targetLlmConfigId = null;
    try {
        const providerData = llmConfigs?.[currentProvider.value];
        const idiomData = providerData?.[currentIdiom.value];
        const modelEntry = idiomData?.find(m => m.model_name === currentModel.value); // Find by model *name*
        if (modelEntry) {
            targetLlmConfigId = modelEntry.llm_config_id;
            console.log(`[handleSetDefaultLlm] Found target ID based on selection (${currentProvider.value}/${currentIdiom.value}/${currentModel.value}):`, targetLlmConfigId);
        } else {
             // This *shouldn't* happen if the dropdowns are populated correctly, but good to check
            console.error("[handleSetDefaultLlm] Could not find matching llm_config_id for current selection:", { provider: currentProvider.value, idiom: currentIdiom.value, model: currentModel.value });
            setLlmConfigModalError("Internal error: Could not determine the configuration ID.");
            return;
        }
    } catch (lookupError) {
        console.error("[handleSetDefaultLlm] Error during llm_config_id lookup:", lookupError);
        setLlmConfigModalError("Internal error: Failed to lookup configuration ID.");
        return;
    }
    // --- End FIX ---

    // 2. Validate API Key (if required)
    let apiKeyToSave = null;
    if (isApiKeyInputVisible) {
        if (!currentApiKey.trim()) {
            setApiKeyError(`API Key is required for ${currentProvider.label}.`);
            return;
        }
        apiKeyToSave = currentApiKey.trim();
    }

    // 3. Start loading states
    setIsSettingDefault(true);
    if (apiKeyToSave) setIsApiKeyLoading(true);

    let apiKeySaved = false;

    try {
         // 4. Upsert API Key (if needed)
         if (apiKeyToSave) {
             console.log(`[handleSetDefaultLlm] Saving API Key for provider: ${currentProvider.value}`);
             const apiKeyResult = await upsertUserApiKey(currentProvider.value, apiKeyToSave);
             setCurrentApiKey(""); // Clear key from state REGARDLESS
             setIsApiKeyLoading(false);

             if (!apiKeyResult?.status) {
                 console.error("[handleSetDefaultLlm] Failed to save API key:", apiKeyResult?.message);
                 setApiKeyError(apiKeyResult?.message || "Failed to save API key.");
                 setIsSettingDefault(false);
                 return;
             }
             console.log("[handleSetDefaultLlm] API Key saved successfully.");
             setApiKeyExists(true);
             setIsApiKeyInputVisible(false);
             apiKeySaved = true;
         } else {
             apiKeySaved = true; // No key needed saving
         }

         // 5. Update User Settings (using the *looked-up* targetLlmConfigId)
         if (apiKeySaved) {
             // --- Use the looked-up ID ---
             console.log("[handleSetDefaultLlm] Saving default LLM ID:", targetLlmConfigId);
             const settingsResult = await updateUserSettings({
                 defaultLlmConfigId: targetLlmConfigId,
             });
             // --------------------------

             if (settingsResult?.status) {
                 await fetchUserSettings(); // Refresh context state
                 setSuccessMessage("Default LLM preference saved successfully!");
                 setTimeout(() => setSuccessMessage(""), 3000);
             } else {
                 console.error("[handleSetDefaultLlm] Failed to save default LLM preference:", settingsResult?.message);
                 setLlmConfigModalError(settingsResult?.message || "Failed to save default LLM preference.");
             }
         }

    } catch (error) {
         console.error("[handleSetDefaultLlm] Unexpected error:", error);
         setLlmConfigModalError("An unexpected error occurred while saving settings.");
         setIsApiKeyLoading(false); // Ensure loading is off
         setCurrentApiKey(""); // Clear potentially sensitive state
    } finally {
         setIsSettingDefault(false);
    }
  };

    // --- NEW: Determine if the current selection matches the saved default ---
    const isOverallLoading = llmConfigModalLoading || isSettingDefault 
                             || isApiKeyLoading || isDeletingApiKey;

    // --- REVISED: Determine if the current selection matches the saved default ---
    const isCurrentSelectionDefault = useMemo(() => {
        // To match, we need a current selection AND a saved default
        if (!currentProvider?.value || !currentIdiom?.value || !currentModel?.value || !userSettings?.defaultLlmConfigId) {
            // If any part of current selection is missing, or no default is saved, it can't be the default
            return false;
        }
  
        // Compare the VALUE of each selected item with the corresponding default setting name
        const isMatch =
            currentProvider.value === userSettings.defaultProviderName &&
            currentIdiom.value === userSettings.defaultIdiom &&
            currentModel.value === userSettings.defaultModelName; // Compare model *name* (value)
  
        console.log("[isCurrentSelectionDefault Memo]", {
            defaultProvider: userSettings.defaultProviderName,
            defaultIdiom: userSettings.defaultIdiom,
            defaultModel: userSettings.defaultModelName,
            currentProvider: currentProvider?.value,
            currentIdiom: currentIdiom?.value,
            currentModel: currentModel?.value,
            isMatch: isMatch
        });
  
        return isMatch;
    }, [
        currentProvider?.value,
        currentIdiom?.value,
        currentModel?.value, // Depends on the selected model's value (name)
        userSettings?.defaultLlmConfigId, // Still need to know *if* a default exists
        userSettings?.defaultProviderName,
        userSettings?.defaultIdiom,
        userSettings?.defaultModelName // Depends on the saved default names
    ]);
  

  // --- JSX ---
  return (
    <Modal
      isOpen={isLlmConfigModalOpen}
      onRequestClose={handleCloseRequest}
      className={styles.modalContent}
      overlayClassName={styles.modalOverlay}
      contentLabel="AI Configuration Menu"
      shouldCloseOnOverlayClick={!(llmConfigModalLoading || isSettingDefault)}
      shouldCloseOnEsc={!(llmConfigModalLoading || isSettingDefault)}
      aria={{ modal: "true" }}
    >
      <h2 id="llmConfigModalTitle">AI Configuration Menu</h2>

       {/* Combined Spinner Logic */}
       {(llmConfigModalLoading || isSettingDefault || isApiKeyLoading) && (
        <Spinner
          message={
             isDeletingApiKey ? "Deleting API Key..."
             : isApiKeyLoading ? "Loading API Key..."
             : isSettingDefault ? "Saving Default..."
             : "Saving Changes..." // For prompt saves
          }
        />
      )}
      
      {/* Section 1: LLM Default Selection */}
      <label className={styles.sectionLabel}>
        Select Your Default LLM Preference:
      </label>
      <div className={styles.configRow}>
        <div className={styles.configItem}>
          <label htmlFor="llmProviderSelect">Provider:</label>
          <Select
            inputId="llmProviderSelect"
            options={providerOptions}
            value={currentProvider}
            onChange={handleProviderChange}
            styles={customSelectStyles}
            isDisabled={llmConfigModalLoading || isSettingDefault}
            placeholder="Provider..."
            isClearable={true}
          />
        </div>
        <div className={styles.configItem}>
          <label htmlFor="llmIdiomSelect">Idiom:</label>
          <Select
            inputId="llmIdiomSelect"
            options={availableIdiomsForProvider}
            value={currentIdiom}
            onChange={handleIdiomChange}
            styles={customSelectStyles}
            isDisabled={!currentProvider || llmConfigModalLoading || isSettingDefault}
            placeholder="Idiom..."
            key={`idiom-${currentProvider?.value || "np"}`}
            isClearable={false}
          />
        </div>
        <div className={styles.configItem}>
          <label htmlFor="llmModelSelect">Model:</label>
          <Select
            inputId="llmModelSelect"
            options={modelOptions}
            value={currentModel}
            onChange={handleModelChange}
            styles={customSelectStyles}
            isDisabled={
              !currentProvider || !currentIdiom || llmConfigModalLoading || isSettingDefault
            }
            placeholder="Model..."
            key={`model-${currentProvider?.value || "np"}-${currentIdiom?.value || "ni"}`}
            isClearable={true}
          />
        </div>
        {/* Set Default Button */}
        <div className={styles.configItem} style={{ justifyContent: 'flex-end', flexGrow: 0.5 }}>
            {/* --- Uses the REVISED isCurrentSelectionDefault logic --- */}
            <button
              onClick={handleSetDefaultLlm}
              className={`${styles.actionButton} ${styles.setDefaultButton}`}
              disabled={
                isOverallLoading || // Still disable during any loading
                !currentModel?.value || // Still disable if no model is selected
                // Disable ONLY IF the input is visible AND empty
                (isApiKeyInputVisible && !currentApiKey.trim()) ||
                // Disable ONLY IF it's the default AND the API key input is NOT visible (meaning key exists/not needed)
                (isCurrentSelectionDefault && !isApiKeyInputVisible)
              }
              title={
                isOverallLoading ? "Processing..."
                : !currentModel?.value ? "Select Provider, Idiom, and Model first"
                // Title when input is visible but empty
                : (isApiKeyInputVisible && !currentApiKey.trim()) ? `Enter API Key for ${currentProvider?.label || 'selected provider'}`
                // Title when it's the default AND key exists (input hidden)
                : (isCurrentSelectionDefault && !isApiKeyInputVisible) ? "This is already your default setting (API key set)"
                // Default title (covers saving new default OR saving key for existing default)
                : "Save current LLM selection and API key (if needed) as your default"
              }
            >
              Set Default
            </button>
        </div>
      </div>

      {/* --- NEW API Key Input Section --- */}
      {currentProvider && ( // Only show if a provider is selected
        <div className={styles.apiKeyRow}>
            <label htmlFor="apiKeyInput">
                API Key for {currentProvider.label}:
            </label>
             <div className={styles.apiKeyInputContainer}>
                {isApiKeyInputVisible && (
                    <input
                        id="apiKeyInput"
                        type="password" // Use password type
                        className={styles.apiKeyInput}
                        value={currentApiKey}
                        onChange={handleApiKeyChange}
                        placeholder={`Enter your ${currentProvider.label} API key...`}
                        disabled={isOverallLoading}
                        aria-describedby="apiKeyErrorHint" // For accessibility
                    />
                )}
                {/* Status Indicator */}
                {isApiKeyLoading ? (
                     <span className={styles.apiKeyStatus}>Checking...</span>
                ) : apiKeyExists && !isApiKeyInputVisible ? (
                     <span className={styles.apiKeyStatus}>✓ Key is set</span>
                ) : !apiKeyExists && !isApiKeyInputVisible && !isApiKeyLoading ? (
                    // This case means provider selected, check complete, key exists, input hidden
                     <span className={styles.apiKeyStatus}>✓ Key is set</span>
                ): !apiKeyExists && isApiKeyInputVisible && !isApiKeyLoading ? (
                     <span className={styles.apiKeyStatus}>Required</span>
                ): null /* Add more states if needed */}
                {/* --- NEW Delete Button --- */}
                {/* Show delete button only if key exists and input is hidden */}
                {apiKeyExists && !isApiKeyInputVisible && (
                    <button
                        onClick={handleDeleteApiKey}
                        className={`${styles.actionButton} ${styles.deleteButton}`} // Add styles for deleteButton
                        disabled={isOverallLoading || isDeletingApiKey} // Disable during any loading
                        title={`Delete saved API key for ${currentProvider.label}`}
                        style={{marginLeft: '10px', padding: '4px 8px', fontSize: '0.8em'}} // Quick inline style example
                    >
                        Delete Key
                    </button>
                )}
             </div>
             {/* Error Display */}
             {apiKeyError && (
                 <div id="apiKeyErrorHint" className={styles.apiKeyError}>{apiKeyError}</div>
             )}
        </div>
      )}


      {/* Section 2: Template Selection */}
      <label className={styles.sectionLabel}>
        Edit Your Prompt Overrides for a Task:
      </label>
      <div className={styles.configRow}>
        <div className={styles.configItemFull}>
          <label htmlFor="templateSelect">Select Task Template:</label>
          <Select
            inputId="templateSelect"
            options={templateOptions}
            value={
              templateOptions.find((o) => o.value === selectedTaskType) || null
            }
            onChange={handleTemplateChange}
            styles={customSelectStyles}
            isDisabled={llmConfigModalLoading || isSettingDefault}
            placeholder="Select Template to Edit Prompts..."
            isClearable={true}
          />
        </div>
      </div>

      {/* Section 3: Editable Overrides */}
      {selectedTaskType && (
        <div className={styles.editFieldsContainer}>
          <div className={styles.formGroup}>
            <label htmlFor="promptInput">Your Prompt Override:</label>
            <textarea
              id="promptInput"
              value={currentPrompt}
              onChange={(e) => setCurrentPrompt(e.target.value)}
              rows="8"
              disabled={llmConfigModalLoading || isSettingDefault}
              placeholder="Enter your custom prompt for this task..."
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="followUpTextInput">
              Your Follow-up Task Text Override:
            </label>
            <textarea
              id="followUpTextInput"
              value={currentFollowUpText}
              onChange={(e) => setCurrentFollowUpText(e.target.value)}
              rows="2"
              disabled={llmConfigModalLoading || isSettingDefault}
              placeholder="Enter your custom text for the ToDo list item..."
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="followUpPromptInput">
              Your Follow-up Email Prompt Override:
            </label>
            <textarea
              id="followUpPromptInput"
              value={currentFollowUpPrompt}
              onChange={(e) => setCurrentFollowUpPrompt(e.target.value)}
              rows="3"
              disabled={llmConfigModalLoading || isSettingDefault}
              placeholder="Enter your custom prompt for the follow-up email..."
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="followUpDaysOffsetInput">
            Your Number of Days For Follow-up Task:
            </label>
            <textarea
              id="followUpDaysOffsetInput"
              value={currentFollowUpDaysOffset}
              onChange={(e) => setCurrentFollowUpDaysOffset(e.target.value)}
              rows="1"
              disabled={llmConfigModalLoading || isSettingDefault}
              placeholder="Enter number of days from today for the follow-up task..."
              style={{ width: '240px' }} // Keep input reasonably sized
            />
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className={styles.modalActions}>
        {successMessage && (
          <div className={styles.modalSuccess}>{successMessage}</div>
        )}
        {llmConfigModalError && (
        <div className={styles.modalError}>Error: {llmConfigModalError}</div>
        )}
        <button
          onClick={handleSaveChanges}
          className={`${styles.actionButton} ${styles.saveButton}`}
          disabled={!selectedTaskType || llmConfigModalLoading || isSettingDefault}
        >
          {llmConfigModalLoading ? "Saving..." : "Save Prompt Changes"}
        </button>
        <button
          onClick={handleCloseRequest}
          className={`${styles.actionButton} ${styles.closeButton}`}
          disabled={llmConfigModalLoading || isSettingDefault}
        >
          Close
        </button>
      </div>
    </Modal>
  );
};

export default LlmConfigModal;
